import React from "react";
import { SelectHTMLAttributes } from "react";
import { FieldError } from "react-hook-form";
import { Error } from "./Error";
import { Label } from "./Label";
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Option = {
  label: string;
  value: string;
};

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string | FieldError;
  options?: Option[];
  placeholder?: string;
  value?: string;
};

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (props, ref) => {
    const { label, id, required, error, options, placeholder, name, defaultValue, value, onChange, ...rest } = props;

    return (
      <React.Fragment>
        <Label label={label} htmlFor={id} required={required} />
        {/* Hidden native select for react-hook-form compatibility */}
        <select
          id={id}
          name={name}
          ref={ref}
          defaultValue={defaultValue}
          onChange={onChange}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <ShadcnSelect
          value={value || (defaultValue as string | undefined) || undefined}
          onValueChange={(next) => {
            // Sync with the hidden native select for react-hook-form
            const nativeSelect = document.getElementById(id ?? "") as HTMLSelectElement | null;
            if (nativeSelect) {
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                HTMLSelectElement.prototype,
                "value"
              )?.set;
              nativeInputValueSetter?.call(nativeSelect, next);
              nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }}
        >
          <SelectTrigger
            className={`w-full ${
              error
                ? "border-danger focus:ring-danger"
                : "border-gray-200"
            }`}
          >
            <SelectValue placeholder={placeholder ?? "Sélectionner..."} />
          </SelectTrigger>
          <SelectContent>
            {options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </ShadcnSelect>

        <Error error={error} />
      </React.Fragment>
    );
  }
);
